import { useEffect, useContext, useState, useCallback } from 'react';
import Balls from '../components/Balls';
import { AppContext } from '../context';
import { Locale } from '../lib/localisation';
import { completePayment } from '../lib/paymentService';
import { getParameterByName, PARAMS, wait } from '../lib/util';
import './CompleteThreeDS.css';

export default function CompleteThreeDS(): JSX.Element {
  const context = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');
  const [interceptUrl, setInterceptUrl] = useState<string | null>(null);

  const parseDefaultErrorMessage = useCallback(
    (msg?: string) =>
      context.locale === Locale.EN
        ? `Uhoh Something went wrong: ${msg}`
        : `Uffda Noe gikk galt: ${msg}`,
    [context.locale]
  );

  const redirect = useCallback(() => {
    interceptUrl && window.location.replace(interceptUrl);
  }, [interceptUrl]);

  const sendComplete3dsRequest = useCallback(async () => {
    setLoading(true);
    const data = {
      // Only sent by Adyen Checkout API v66:
      MD: getParameterByName(PARAMS.MD) || undefined,
      PaRes: getParameterByName(PARAMS.PARES) || undefined,
      // Only sent by Adyen Checkout API v67:
      redirectResult: getParameterByName(PARAMS.REDIRECT_RESULT) || undefined,
    };
    try {
      const [err, response] = await Promise.all([wait(1000), completePayment(context, data)]).then(
        ([, v]) => v
      );
      setComplete(true);
      setLoading(false);
      if (err) {
        setError(parseDefaultErrorMessage(err?.message));
        return;
      }
      setInterceptUrl(response?.interceptUrl as string);
      if (response?.adyenData.resultCode === 'Refused') {
        const message = response.message;
        setError(message && message.length > 0 ? message : parseDefaultErrorMessage());
        return;
      }
      response && response.interceptUrl && window.location.replace(response.interceptUrl);
    } catch (e) {
      setError(parseDefaultErrorMessage((e as Error)?.message));
    } finally {
      setLoading(false);
    }
  }, [context, parseDefaultErrorMessage]);

  useEffect(() => {
    if (complete || loading) {
      return;
    }
    sendComplete3dsRequest();
  }, [complete, sendComplete3dsRequest, loading]);

  return loading || error?.length === 0 ? (
    <Balls />
  ) : (
    <div className="threeds">
      <div className="error-container">
        <p>{error}</p>
      </div>
      <div className="splitter"></div>
      <div className="close-button-group">
        <button onClick={redirect}>{context.texts.closeButton}</button>
      </div>
    </div>
  );
}
